/**
 * Copy FIJO del blog (B64 del spec del blog).
 *
 * Solo lo que el front rotula por sí mismo. El contenido editorial —título,
 * extracto, cuerpo, autor, bio— es contenido de base de datos y llega en el
 * payload, no vive aquí.
 *
 * Las cadenas se movieron verbatim desde las plantillas el 24/09/2026, sin
 * reescribirlas: por eso tutean. El tono (tuteo frente a voseo es-CR) es la
 * P18 del spec y sigue abierta.
 *
 * Fijado literalmente por `blog.constants.spec.ts`.
 */
export const BLOG_COPY = {
  // Migas
  breadcrumbHome: 'Inicio',
  breadcrumbBlog: 'Blog',

  // Listado
  listTitle: 'Blog de Perrón Store',
  allGames: 'Todos',
  clearFilters: 'Limpiar filtros',
  emptyMascotAlt: 'Perrón confundido',
  emptyTitle: 'No encontramos artículos con estos filtros',
  emptyText: 'Prueba con otros filtros o explora todos los artículos.',
  emptyCta: 'Ver todos los artículos',
  newsletterPlaceholder: 'Tu correo electrónico',
  newsletterCta: 'Suscribirme',
  newsletterHeading: 'Recibe novedades y ofertas exclusivas',
  newsletterDescription:
    'Suscríbete al newsletter de Perrón Store y entérate primero de nuevos sets, torneos y artículos del blog.',

  // Barra de filtros
  categoryPlaceholder: 'Categoría',
  tagPlaceholder: 'Etiqueta',
  allCategories: 'Todas las categorías',
  allTags: 'Todos los tags',
  searchPlaceholder: 'Buscar artículos...',
  /** Contador de resultados; conserva el plural que ya pintaba la plantilla. */
  resultsCount: (n: number): string => `${n} artículo${n === 1 ? '' : 's'}`,

  // Cards
  featuredBadge: 'Destacado',
  /** Sufijo fijado por B16: la API emite el entero, el front pone el texto. */
  readTime: (minutes: number): string => `${minutes} min de lectura`,

  // Artículo
  relatedPostsTitle: 'Artículos relacionados',
  comingSoonMascotAlt: 'Mascota de Perrón Store leyendo',
  comingSoonTitle: 'Artículo en preparación',
  comingSoonText: 'Este artículo estará disponible pronto. Mientras tanto, explora el resto del blog.',
  backToBlog: 'Volver al blog',
  notFoundMascotAlt: 'Mascota de Perrón Store perdida',
  notFoundTitle: 'Artículo no encontrado',
  notFoundText: 'Este artículo no existe o fue eliminado.',

  // Estado de error: la API no respondió (W5, B123/B130). Calcan el «No
  // pudimos cargar… Intenta de nuevo.» de eventos y de la landing de juego.
  listLoadError: 'No pudimos cargar los artículos. Intenta de nuevo.',
  articleLoadError: 'No pudimos cargar este artículo. Intenta de nuevo.',
  retry: 'Reintentar',
  errorMascotAlt: 'Mascota de Perrón Store con cara de error',

  // Índice y compartir
  tocLabel: 'Tabla de contenidos',
  tocHeading: 'En este artículo',
  share: 'Compartir',
  shareTwitter: 'Compartir en Twitter',
  shareFacebook: 'Compartir en Facebook',
  shareWhatsApp: 'Compartir en WhatsApp',
  copyUrl: 'Copiar URL',
  urlCopied: 'URL copiada',

  // Pie del artículo
  articleNavLabel: 'Navegación entre artículos',
  prevArticle: '← Anterior',
  nextArticle: 'Siguiente →',

  // Barra lateral
  sidebarCategories: 'Categorías',
  sidebarStoreCategories: 'Explorar en tienda',
  sidebarRelatedProducts: 'Productos relacionados',
  sidebarNewsletterPlaceholder: 'tu@email.com',
  sidebarNewsletterCta: 'OK',
  sidebarNewsletterHeading: 'Newsletter',
  sidebarNewsletterDescription: 'Recibe artículos y ofertas',

  // Producto inline
  inlineProductLabel: 'Producto mencionado en el artículo',
  /** Sin consumidor desde W2 (B70): el botón se oculta hasta cablearlo (P17). */
  inlineProductCta: 'Agregar',

  // Productos del post (relacionados e inline). Verbatim de `product-card`.
  productSoldOut: 'Agotado',

  // Autor
  authorArticleCount: (n: number): string => `${n} artículos publicados`,

  // SEO del listado. La descripción reutiliza verbatim el subtítulo del antiguo
  // bloque de blog del home (borrado en W5, B128) en vez de escribir copy nuevo.
  seoTitle: 'Blog',
  seoDescription: 'Guías, análisis y noticias del mundo TCG en Costa Rica.',

  // SEO del listado filtrado (B66). Las dos de juego son verbatim del título y
  // el subtítulo de `game-blog-posts`; las de taxonomía son copy nuevo, a la
  // espera de la pasada de tono con Omar (P21, junto a P18).
  seoTitleGame: (game: string): string => `Blog de ${game}`,
  seoDescriptionGame: (game: string): string => `Guías, análisis y noticias sobre ${game}.`,
  seoTitleTaxonomy: (name: string): string => `${name} · Blog`,
  seoDescriptionCategory: (category: string): string =>
    `Artículos de la categoría ${category} en el blog de Perrón Store.`,

  // Comentarios públicos (W4, B115). Tutean como el resto de la web (P18 sigue
  // abierta). Los textos que manda la API —el `message` del 201, los 422 y el
  // 429— son datos: se pintan tal cual y no viven aquí.
  comments: {
    heading: 'Comentarios',
    count: (n: number): string => `${n} comentario${n === 1 ? '' : 's'}`,
    empty: 'Todavía no hay comentarios. Sé quien abra la conversación.',
    loadError: 'No pudimos cargar los comentarios.',
    retry: 'Reintentar',
    loadMore: 'Cargar más comentarios',
    staffBadge: 'Perrón Store',
    edited: 'editado',
    reply: 'Responder',
    replyingTo: (name: string): string => `Respondiendo a ${name}`,
    cancelReply: 'Cancelar',
    formHeading: 'Deja tu comentario',
    moderationNote: 'Los comentarios se revisan antes de publicarse.',
    commentingAs: (name: string): string => `Comentas como ${name}`,
    nameLabel: 'Nombre',
    emailLabel: 'Correo electrónico',
    emailHint: 'No se publica.',
    bodyLabel: 'Comentario',
    bodyCounter: (n: number, max: number): string => `${n}/${max}`,
    submit: 'Enviar comentario',
    sentFallback: '¡Gracias! Tu comentario quedó pendiente de revisión.',
    errors: {
      bodyRequired: 'Escribe tu comentario.',
      bodyTooLong: 'Tu comentario es demasiado largo (máximo 2000 caracteres).',
      nameRequired: 'Dinos cómo te llamas.',
      nameTooLong: 'Tu nombre es demasiado largo (máximo 120 caracteres).',
      emailRequired: 'Déjanos tu correo.',
      emailInvalid: 'Ese correo no parece válido.',
      closed: 'Este artículo ya no admite comentarios.',
      generic: 'No pudimos enviar tu comentario. Inténtalo de nuevo.',
    },
  },
} as const;
